import axiosInstance from '../utils/AxiosConfig';

export const getProducts = async (locationId, page = 1, pageSize = 10, searchTerm = '', options = {}) => {
  const params = new URLSearchParams({
    locationId: locationId.toString(),
    page: page.toString(),
    pageSize: pageSize.toString()
  });
  
  if (searchTerm && typeof searchTerm === 'string' && searchTerm.trim()) {
    params.append('search', searchTerm.trim());
  }
  if (options.sortField) params.append('sortField', options.sortField);
  if (options.sortDirection) params.append('sortDirection', options.sortDirection);
  if (options.filterField) params.append('filterField', options.filterField);
  if (options.filterOperator) params.append('filterOperator', options.filterOperator);
  if (options.filterValue !== undefined && options.filterValue !== null && options.filterValue !== '') {
    params.append('filterValue', options.filterValue.toString());
  }
  
  const response = await axiosInstance.get(`/products?${params}`);
  return response.data.data;
};

// true cuando todas las sucursales facturan en Literal E: la tasa de IVA del producto no se usa y el formulario la esconde.
export const getAllLocationsLiteralE = async () => {
  const response = await axiosInstance.get('/products/iva-regime');
  return response.data.data?.all_literal_e === true;
};

export const getProductById = async (productId, locationId) => {
  const response = await axiosInstance.get(`/products/${productId}`, {
    params: {
      locationId: locationId
    }
  });
  return response.data.data;
};

export const getProductPriceHistory = async (productId, locationId) => {
  const response = await axiosInstance.get(`/products/${productId}/price-history`, {
    params: { locationId }
  });
  return response.data.data;
};

export const getProductBySku = async (locationId, sku) => {
  try {
    const response = await axiosInstance.get('/products/by_sku', {
      params: {
        locationId,
        sku: String(sku).trim()
      }
    });
    return response.data?.data ?? response.data;
  } catch {
    return null;
  }
};

export const addProductsToCategoryView = async (locationId, categoryViewId, productIds) => {
  const response = await axiosInstance.post('/products/category_view', {
    location_id: locationId,
    category_view_id: categoryViewId,
    product_ids: productIds
  });
  return response.data.data;
};

export const removeProductFromCategoryView = async (locationId, productId, categoryViewId) => {
  const response = await axiosInstance.delete('/products/category_view', {
    params: {
      location_id: locationId,
      product_id: productId,
      category_view_id: categoryViewId
    }
  });
  return response.data;
};

export const getSomeProducts = async (locationId, productIds) => {
  const response = await axiosInstance.post('/products/some_products', {
    location_id: locationId,
    product_ids: productIds,
  });
  return response.data.data;
};

export const getProductsAndPreparations = async (locationId) => {
  const response = await axiosInstance.get('/products/products_and_preparations', {
    params: {
      locationId: locationId
    }
  });
  return response.data.data;
};

/** Lista ligera (id, name, batch?, unit?) para selector de producción; carga rápida. */
export const getProductsAndPreparationsLight = async (locationId) => {
  const response = await axiosInstance.get('/products/products_and_preparations_light', {
    params: { locationId }
  });
  return response.data.data;
};

export const getProductsForPackaging = async (preparationId) => {
  const response = await axiosInstance.get(`/products/product_that_use_preparation/${preparationId}`);
  return response.data.data;
};

export const addProduct = async (product, locationId) => {
  const request = mapProductDataToBackend(product, locationId)
  const response = await axiosInstance.post(`/products`, request);
  return response.data.data;
};

export const updateProduct = async (id, product, locationId) => {
  const request = mapProductDataToBackend(product, locationId);
  console.log('[ProductService] updateProduct REQUEST', { productId: id, locationId, body: request });
  try {
    const response = await axiosInstance.put(`/products/${id}`, request);
    console.log('[ProductService] updateProduct RESPONSE', { status: response.status, data: response.data });
    return response.data.data;
  } catch (err) {
    console.error('[ProductService] updateProduct ERROR', {
      status: err.response?.status,
      statusText: err.response?.statusText,
      data: err.response?.data,
      message: err.message,
    });
    throw err;
  }
};

export const updateStockAndPrice = async (id, request) => {
  const response = await axiosInstance.put(`/products/stockandprice/${id}`, request);
  return response.data.data;
};

export const deleteProduct = async (id) => {
  await axiosInstance.delete(`/products/${id}`);
};

// Método eliminado: uploadPicture - usar ProductImagesService en su lugar

const mapProductDataToBackend = (productData, locationId) => {
  try {
    const {
      name,
      price,
      priceCurrency,
      categoryId,
      subCategoryId,
      productType,
      sku,
      stock,
      suggestedStock,
      minimumStock,
      alias,
      stockControlMode,
      usesLot,
      ivaRate,
      primarySupplier,
      secondarySupplier,
      packagingItems = [],
      contentItems = [],
      toppingItems = [],
      contentDefinedItems = [],
      contentUndefinedItems = [],
      optionalsItems = [],
      maxOptionalSelections
    } = productData;
    const locId = locationId != null && locationId !== '' ? Number(locationId) : null;
    if (locId == null || Number.isNaN(locId)) {
      throw new Error('location_id es requerido y debe ser un número');
    }
    const num = (v) => (v != null && v !== '' && !Number.isNaN(Number(v)) ? Number(v) : 0);
    const mappedData = {
      name: name,
      price: price,
      ...(priceCurrency ? { price_currency: priceCurrency } : {}),
      category_id: categoryId,
      sub_category_id: subCategoryId != null && subCategoryId !== '' ? Number(subCategoryId) : undefined,
      ...(alias?.trim() ? { alias: alias.trim() } : {}),
      stock_control_mode: stockControlMode || 'UNITARY',
      uses_lot: usesLot === true,
      iva_rate: [22, 10, 0].includes(Number(ivaRate)) ? Number(ivaRate) : 22,
      stock: num(stock),
      suggested_stock: num(suggestedStock),
      minimum_stock: num(minimumStock),
      ...(productType ? { product_type: productType } : {}),
      ...(sku ? { sku: sku } : {}),
      ...(primarySupplier ? { primary_supplier: primarySupplier } : {}),
      ...(secondarySupplier ? { secondary_supplier: secondarySupplier } : {}),
      location_id: locId,
      packaging: packagingItems.map(item => ({
        quantity: item.quantity,
        unit: item.unit,
        reference_type: item.referenceType,
        reference_id_1: item.referenceId1
      })),
      content: contentItems.map(item => ({
        quantity: item.quantity,
        unit: item.unit,
        reference_type: item.referenceType,
        reference_id_1: item.referenceId1
      })),
      toppings: toppingItems.map(item => ({
        quantity: item.quantity,
        unit: item.unit,
        reference_type: item.referenceType,
        reference_id_1: item.referenceId1
      })),
      defined_content: contentDefinedItems.map(item => ({
        quantity: item.quantity,
        unit: item.unit,
        reference_type: item.referenceType,
        reference_id_1: item.referenceId1,
        ...(item.referenceId2 ? { reference_id_2: item.referenceId2 } : {})
      })),
      undefined_content: contentUndefinedItems.map(item => ({
        quantity: item.quantity,
        unit: item.unit,
        reference_type: item.referenceType,
        reference_id_1: item.referenceId1,
        ...(item.referenceId2 ? { reference_id_2: item.referenceId2 } : {})
      })),
      optionals: optionalsItems.filter(o => o.referenceType && o.referenceId1).map((item, idx) => {
        const qty = item.quantity === '' || item.quantity == null ? 1 : Number(item.quantity);
        const u = item.unit === '' || item.unit == null ? 5 : Number(item.unit);
        const extra = item.extraPrice === '' || item.extraPrice == null ? null : Number(item.extraPrice);
        return {
          reference_type: Number(item.referenceType),
          reference_id_1: Number(item.referenceId1),
          quantity: Number.isNaN(qty) ? 1 : qty,
          unit: Number.isNaN(u) ? 5 : u,
          extra_price: extra != null && Number.isNaN(extra) ? null : extra,
          display_order: idx
        };
      }),
      ...(maxOptionalSelections !== '' && maxOptionalSelections != null ? (() => {
        const n = parseInt(maxOptionalSelections, 10);
        return Number.isNaN(n) ? {} : { max_optional_selections: n };
      })() : {})
    };
    return mappedData;
  } catch (error) {
    console.error("Error during mapping data to backend format:", error);
    throw error;  // Re-throw the error to handle it further up in the call stack
  }
};
